import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Language as LanguageIcon,
  RemoveCircleOutline as SkippedIcon,
} from '@mui/icons-material';
import {
  pollUrlDiscoveryCheck,
  reportFeedIssue,
  startUrlDiscoveryCheck,
} from '../../api/contentCompletion';
import {
  CompanyContentHealth,
  UrlDiscoveryResult,
  UrlDiscoveryStatus,
} from '../../types/contentCompletion';
import BatchUrlDiscoveryReviewDialog from './BatchUrlDiscoveryReviewDialog';
import {
  buildUrlDiscoveryIssueRequest,
  getDiscoveryResultSummary,
  shouldReportUrlDiscoveryIssue,
  URL_DISCOVERY_REPORTABLE_STATUSES,
} from './urlDiscoveryUtils';

type ItemStatus =
  | 'queued'
  | 'checking'
  | 'check_ready'
  | 'check_failed'
  | 'no_changes'
  | 'saved'
  | 'error';

interface BatchItem {
  companyId: string;
  companyName: string;
  company: CompanyContentHealth;
  status: ItemStatus;
  discoveryResult?: UrlDiscoveryResult;
  error?: string;
}

interface BatchUrlDiscoveryPanelProps {
  companies: CompanyContentHealth[];
  onClose: () => void;
  onComplete: () => void;
  onIssueReported: () => void;
}

const CHECK_STATUS_META: Record<
  UrlDiscoveryStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  ok: { label: 'Valid', color: 'success' },
  no_changes: { label: 'No changes', color: 'default' },
  discovered: { label: 'Discovered', color: 'info' },
  repaired: { label: 'Repaired', color: 'warning' },
  website_not_found: { label: 'No website', color: 'error' },
  broken_no_replacement: { label: 'No replacement', color: 'error' },
  error: { label: 'Error', color: 'error' },
};

const STATUS_META: Record<
  ItemStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  queued: { label: 'Queued', color: 'default' },
  checking: { label: 'Checking…', color: 'info' },
  check_ready: { label: 'Ready to review', color: 'warning' },
  check_failed: { label: 'Check failed', color: 'error' },
  no_changes: { label: 'No changes', color: 'default' },
  saved: { label: 'Saved', color: 'success' },
  error: { label: 'Error', color: 'error' },
};

/** Proposed updates, or failures that still need manual URL entry in review. */
const isReviewable = (result: UrlDiscoveryResult): boolean => {
  if (result.status !== 'success') return false;
  if (result.apply_fields.length > 0) return true;
  return URL_DISCOVERY_REPORTABLE_STATUSES.has(result.check_status);
};

const BatchUrlDiscoveryPanel: React.FC<BatchUrlDiscoveryPanelProps> = ({
  companies,
  onClose,
  onComplete,
  onIssueReported,
}) => {
  const [items, setItems] = useState<BatchItem[]>(() =>
    companies.map((company) => ({
      companyId: company.company_id,
      companyName: company.company_name,
      company,
      status: 'queued' as ItemStatus,
    }))
  );
  const [running, setRunning] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const cancelledRef = useRef(false);

  const updateItem = useCallback((companyId: string, patch: Partial<BatchItem>) => {
    setItems((prev) =>
      prev.map((item) => (item.companyId === companyId ? { ...item, ...patch } : item))
    );
  }, []);

  const reportFailedCheck = useCallback(
    async (item: BatchItem, discoveryResult?: UrlDiscoveryResult, error?: string) => {
      const result = discoveryResult;
      if (result && !shouldReportUrlDiscoveryIssue(result)) return;

      try {
        await reportFeedIssue(
          buildUrlDiscoveryIssueRequest(item.companyId, item.companyName, result || {
            run_id: '',
            status: 'failure',
            company_id: item.companyId,
            company_name: item.companyName,
            check_status: 'error',
            current: {},
            proposed: {},
            warnings: [],
            apply_fields: [],
            auto_select: false,
            error,
          }, error)
        );
        onIssueReported();
      } catch {
        /* batch continues */
      }
    },
    [onIssueReported]
  );

  useEffect(() => {
    cancelledRef.current = false;

    const run = async () => {
      for (const seed of companies) {
        if (cancelledRef.current) break;

        updateItem(seed.company_id, { status: 'checking' });

        try {
          const { run_id } = await startUrlDiscoveryCheck({
            company_id: seed.company_id,
            company_name: seed.company_name,
          });

          const discoveryResult = await new Promise<UrlDiscoveryResult>((resolve) => {
            const stop = pollUrlDiscoveryCheck(run_id, (update) => {
              if (update.status !== 'running') {
                stop();
                resolve(update);
              }
            });
          });

          if (cancelledRef.current) break;

          if (discoveryResult.status === 'failure') {
            const batchItem: BatchItem = {
              companyId: seed.company_id,
              companyName: seed.company_name,
              company: seed,
              status: 'check_failed',
            };
            await reportFailedCheck(
              batchItem,
              discoveryResult,
              discoveryResult.error || discoveryResult.summary || 'URL discovery failed'
            );
            updateItem(seed.company_id, {
              status: 'check_failed',
              discoveryResult,
              error: discoveryResult.error || discoveryResult.summary || 'URL discovery failed',
            });
            continue;
          }

          if (isReviewable(discoveryResult)) {
            updateItem(seed.company_id, {
              status: 'check_ready',
              discoveryResult,
            });
            continue;
          }

          if (shouldReportUrlDiscoveryIssue(discoveryResult)) {
            await reportFailedCheck(
              {
                companyId: seed.company_id,
                companyName: seed.company_name,
                company: seed,
                status: 'no_changes',
              },
              discoveryResult
            );
          }

          updateItem(seed.company_id, {
            status: 'no_changes',
            discoveryResult,
          });
        } catch (err: unknown) {
          if (cancelledRef.current) break;
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
            (err as Error)?.message ||
            'URL discovery failed';
          await reportFailedCheck(
            {
              companyId: seed.company_id,
              companyName: seed.company_name,
              company: seed,
              status: 'error',
            },
            undefined,
            msg
          );
          updateItem(seed.company_id, { status: 'check_failed', error: msg });
        }
      }

      if (!cancelledRef.current) setRunning(false);
    };

    run();

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompanySaved = (companyId: string) => {
    updateItem(companyId, { status: 'saved' });
    onComplete();
  };

  const toReviewItem = (item: BatchItem) => ({
    companyId: item.companyId,
    companyName: item.companyName,
    company: item.company,
    discoveryResult: item.discoveryResult as UrlDiscoveryResult,
  });

  const reviewItems = useMemo(
    () =>
      items
        .filter((item) => item.status === 'check_ready' && item.discoveryResult)
        .map(toReviewItem),
    [items]
  );

  const validationItems = useMemo(
    () =>
      items
        .filter(
          (item) =>
            item.discoveryResult &&
            (item.status === 'no_changes' ||
              item.status === 'check_failed' ||
              item.status === 'check_ready')
        )
        .map(toReviewItem),
    [items]
  );

  const total = items.length;
  const finishedCount = items.filter(
    (item) => item.status !== 'queued' && item.status !== 'checking'
  ).length;
  const processing = items.find((item) => item.status === 'checking');
  const readyCount = items.filter((item) => item.status === 'check_ready').length;
  const validationCount = items.filter((item) => item.status === 'no_changes').length;
  const allDone =
    !running &&
    items.every(
      (item) =>
        item.status === 'saved' ||
        item.status === 'check_failed' ||
        item.status === 'no_changes' ||
        item.status === 'error' ||
        item.status === 'check_ready'
    );

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'secondary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LanguageIcon color="secondary" />
          <Typography variant="h6" fontWeight={700}>
            URL discovery — {total} compan{total === 1 ? 'y' : 'ies'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={onClose}>
            {allDone ? 'Done' : 'Dismiss'}
          </Button>
        </Box>

        <LinearProgress
          variant="determinate"
          value={total === 0 ? 0 : (finishedCount / total) * 100}
          sx={{ mb: 1, borderRadius: 1 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {running ? (
            processing ? (
              <>
                Discovering URLs {finishedCount + 1}/{total}:{' '}
                <strong>{processing.companyName}</strong>…
              </>
            ) : (
              'Starting URL discovery…'
            )
          ) : (
            <>
              Discovery complete.
              {readyCount > 0
                ? ` ${readyCount} ready to review (including cases needing manual URLs).`
                : ''}
              {validationCount > 0 ? ` ${validationCount} validated with no changes.` : ''}
            </>
          )}
        </Typography>

        {readyCount > 0 && !running && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Review and edit website / feed URLs before saving to Neo4j. You can paste known-good
            links when discovery found nothing.
          </Alert>
        )}

        {!running &&
          items.some(
            (item) =>
              item.status === 'check_failed' ||
              (item.discoveryResult ? shouldReportUrlDiscoveryIssue(item.discoveryResult) : false)
          ) && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Failed or unresolved URL discoveries were automatically added to the URL / feed issues
              list.
            </Alert>
          )}

        {!running && (readyCount > 0 || validationCount > 0) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {readyCount > 0 && (
              <Button variant="contained" color="secondary" onClick={() => setReviewOpen(true)}>
                Review updates ({readyCount})
              </Button>
            )}
            {validationItems.length > 0 && (
              <Button variant="outlined" color="secondary" onClick={() => setValidationOpen(true)}>
                View validation ({validationItems.length})
              </Button>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
          {items.map((item) => {
            const meta = STATUS_META[item.status];
            const checkMeta = item.discoveryResult
              ? CHECK_STATUS_META[item.discoveryResult.check_status]
              : null;
            const secondary =
              item.error ||
              (item.discoveryResult ? getDiscoveryResultSummary(item.discoveryResult) : undefined);
            return (
              <ListItem key={item.companyId} alignItems="flex-start">
                <Box
                  sx={{
                    mr: 1.5,
                    mt: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    width: 24,
                    justifyContent: 'center',
                  }}
                >
                  {item.status === 'checking' && <CircularProgress size={18} />}
                  {item.status === 'saved' && <CheckCircleIcon color="success" fontSize="small" />}
                  {(item.status === 'no_changes' || item.status === 'queued') && (
                    <SkippedIcon color="disabled" fontSize="small" />
                  )}
                  {(item.status === 'check_failed' || item.status === 'error') && (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                </Box>
                <ListItemText
                  primary={item.companyName}
                  secondary={secondary}
                  secondaryTypographyProps={{
                    color: item.error ? 'error' : 'textSecondary',
                    sx: { whiteSpace: 'normal' },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {checkMeta && (
                    <Chip
                      label={checkMeta.label}
                      color={checkMeta.color}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Chip label={meta.label} color={meta.color} size="small" variant="outlined" />
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Paper>

      {reviewOpen && reviewItems.length > 0 && (
        <BatchUrlDiscoveryReviewDialog
          open
          onClose={() => setReviewOpen(false)}
          items={reviewItems}
          onCompanySaved={handleCompanySaved}
          onComplete={onComplete}
          onIssueResolved={onIssueReported}
        />
      )}

      {validationOpen && validationItems.length > 0 && (
        <BatchUrlDiscoveryReviewDialog
          open
          readOnly
          onClose={() => setValidationOpen(false)}
          items={validationItems}
          title={`URL validation — ${validationItems.length} companies`}
          onIssueResolved={onIssueReported}
        />
      )}
    </>
  );
};

export default BatchUrlDiscoveryPanel;
